import PageHero from "@/components/PageHero";
import { IMAGES } from "@/lib/site";
import { BookOpen, FlaskConical, Laptop, Languages, HeartHandshake, Calculator, Baby, Sparkles } from "lucide-react";

const programs = [
  {
    icon: Baby,
    title: "Pre-Primary — Playgroup, Nursery, LKG & UKG",
    desc: "Joyful early learning with phonics, numbers, rhymes and play-based discovery for our youngest students.",
    highlight: true,
  },
  { icon: BookOpen, title: "Primary School (1st – 5th)", desc: "Foundational learning through play, stories and activity-based methods." },
  { icon: Calculator, title: "Upper Primary (6th – 7th)", desc: "Concept-driven academics with focus on Math, Science and Languages." },
  { icon: FlaskConical, title: "High School (8th – 10th)", desc: "State board curriculum with intensive board exam preparation." },
  { icon: Laptop, title: "Computer Education", desc: "Digital literacy from primary level with modern computer lab." },
  { icon: Languages, title: "Language Programs", desc: "Kannada, Hindi and English — strong tri-lingual foundation." },
  { icon: HeartHandshake, title: "Value Education", desc: "Daily moral education sessions, cultural and value-based learning." },
];

export default function AcademicsPage() {
  return (
    <div data-testid="academics-page">
      <PageHero
        eyebrow="ACADEMICS"
        title="Comprehensive Curriculum"
        subtitle="A well-rounded education from Playgroup all the way to Class 10 — and PUC coming soon."
      />

      {/* PUC Coming Soon banner */}
      <section className="py-10 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div
            data-testid="puc-banner"
            className="relative overflow-hidden rounded-3xl border border-gold/40 bg-gradient-to-r from-royal via-royal to-royal-dark text-white p-8 md:p-10"
          >
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-gold/30 blur-3xl" />
            <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full bg-gold/10 blur-3xl" />
            <div className="relative flex flex-col md:flex-row items-start md:items-center gap-5 md:gap-8">
              <div className="w-16 h-16 shrink-0 rounded-2xl bg-gold text-royal-dark flex items-center justify-center shadow-lg">
                <Sparkles size={28} />
              </div>
              <div className="flex-1">
                <span className="inline-block bg-gold text-royal-dark text-[11px] font-bold tracking-[0.25em] px-3 py-1 rounded-full">COMING SOON</span>
                <h2 className="font-serif-display text-3xl md:text-4xl font-bold mt-3">PUC — Pre-University College</h2>
                <p className="text-gray-200 mt-2 md:max-w-2xl">
                  We are proud to announce that <span className="text-gold font-semibold">Pre-University College (Class 11 & 12)</span> is launching soon at Royal English Medium High School. Continue your child's journey with us all the way to college admissions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {programs.map((p, i) => (
            <div
              key={p.title}
              data-testid={`program-${i}`}
              className={`rounded-2xl p-7 hover-lift border ${
                p.highlight
                  ? "bg-gradient-to-br from-gold/10 to-white border-gold/40 shadow-md"
                  : "bg-[#f7f7f7] border-gray-100"
              }`}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${p.highlight ? "bg-gold text-royal-dark" : "bg-gold/15 text-gold-dark"}`}>
                <p.icon size={22} />
              </div>
              {p.highlight && (
                <span className="inline-block bg-gold text-royal-dark text-[10px] font-bold tracking-[0.25em] px-2 py-0.5 rounded-full mb-2">AVAILABLE</span>
              )}
              <h3 className="font-serif-display text-xl font-bold text-royal-dark mb-2">{p.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 px-6 bg-royal text-white">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <img src={IMAGES.sportsPyramid} alt="Sports Pyramid" className="rounded-3xl shadow-2xl w-full h-[420px] object-cover" />
          <div>
            <h2 className="font-serif-display text-4xl font-bold">Beyond the Classroom</h2>
            <div className="gold-divider" />
            <p className="mt-5 text-gray-200 leading-relaxed">
              We believe true learning happens both inside and outside the classroom. From sports and arts to community service
              and leadership programs, our students grow into confident, well-rounded individuals.
            </p>
            <ul className="mt-6 grid grid-cols-2 gap-3 text-sm">
              {["Sports", "Music & Dance", "Drawing & Painting", "Yoga", "Debate Club", "Eco Club"].map(x => (
                <li key={x} className="bg-white/10 px-4 py-2 rounded-full text-center">{x}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
